/**
 * The `createClient` singleton — docs/ARCHITECTURE.md §6 (`data/supabase.ts` in
 * the sketched tree; it lives beside the repositories here because the whole
 * data layer moved to `repo/` in Phase 2b).
 *
 * Session persistence is `localStorage` + auto-refresh, which is what makes
 * ARCHITECTURE §5.2 step 1 (`repo.currentSession()`) able to answer without a
 * network round trip on a warm boot.
 *
 * Everything in `.env.local` is public by design: RLS is the security boundary,
 * not the publishable key (§7).
 */

import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '../types/database.types.ts';

export type QdClient = SupabaseClient<Database>;

export interface SupabaseEnv {
  url: string;
  anonKey: string;
  crewSlug: string;
  /** synthesized login-email domain: `<user_key>@<domain>` */
  emailDomain: string;
}

const env = (k: string): string | undefined =>
  typeof import.meta.env === 'object'
    ? (import.meta.env as Record<string, string | undefined>)[k]
    : undefined;

export function supabaseEnv(): SupabaseEnv {
  return {
    url: env('VITE_SUPABASE_URL') ?? '',
    anonKey: env('VITE_SUPABASE_ANON_KEY') ?? '',
    crewSlug: env('VITE_CREW_SLUG') || 'alpha',
    emailDomain: env('VITE_CREW_EMAIL_DOMAIN') || 'crew.quarterdown.com',
  };
}

/**
 * The auth identity to sign in with — D3, "two identities coexist".
 *
 * TWO POPULATIONS SHARE ONE FIELD. Invited players keep the synthesized
 * `<user_key>@<domain>`; owners who signed up through the wizard have a REAL
 * email and a password they chose. The login form is one input for both, so the
 * rule is the input itself: an `@` means it is already an address and is used
 * as-is, anything else is a player name and gets the domain appended.
 *
 * Before this, the domain was appended unconditionally — which was harmless
 * while every account was an invite, and turns `marcus@example.com` into
 * `marcus@example.com@crew.quarterdown.com` the moment one is not. Nothing
 * about the invited path changes.
 */
export function loginEmail(identity: string, emailDomain: string): string {
  const v = identity.trim().toLowerCase();
  if (v.includes('@')) return v;
  // A NAME IS NOT A KEY, and this is where that used to be assumed.
  //
  // The server derives the login key from the display name by DELETING every
  // character outside `[a-z0-9]` (`_shared/crew.ts#baseKey`, matching
  // `profiles.user_key`'s own CHECK), so "New Owner" is stored as `newowner`.
  // This function appended the domain to whatever was typed — so typing back
  // the name shown on the roster produced `new owner@crew.quarterdown.com`,
  // an address belonging to nobody, and GoTrue's refusal surfaced as WRONG
  // PASSWORD. Anyone whose name contained a space, an apostrophe or an accent
  // could not sign in by name at all (Marcus, staging, 2026-08-20).
  //
  // The two derivations must therefore agree, and the only safe direction is
  // this one mirroring the server's. Deliberately NOT copied from baseKey:
  // its `p${Date.now()}` fallback for a name with no usable characters, which
  // mints a DIFFERENT address on every keystroke. Here that case yields
  // `@domain`, which is refused — the honest answer for an identity that
  // cannot exist.
  const stripped = v.replace(/[^a-z0-9]/g, '');
  // `baseKey` pads a single character to two, because `user_key`'s CHECK
  // demands at least two. A member called "X" is `x1`, so typing "X" has to
  // reach `x1` as well.
  const key = (stripped.length === 1 ? stripped + '1' : stripped).slice(0, 24);
  return key + '@' + emailDomain;
}

let client: QdClient | null = null;

export function createQdClient(e: SupabaseEnv): QdClient {
  if (!e.url || !e.anonKey) {
    throw new Error(
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required for the Supabase ' +
        'repository. Copy app/.env.local.example to app/.env.local, or run ' +
        '`npm run dev:local` to use LocalRepository instead.',
    );
  }
  return createClient<Database>(e.url, e.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: 'qd_auth_v1',
      /**
       * WRITTEN OUT BECAUSE FORGOT PASSWORD DEPENDS ON IT (D-08).
       *
       * `implicit` is auth-js's own default today, so this line changes nothing
       * at runtime — it exists so the dependency is visible. The two flows put
       * an emailed link's payload in DIFFERENT PLACES: implicit returns
       * `#access_token=…&type=recovery` in the fragment, PKCE returns `?code=…`
       * in the query. `lib/joinLink.ts#parseRecovery` reads the fragment, so
       * flipping this to `pkce` would make every password-reset link land on
       * the app and do nothing at all, with no error anywhere.
       *
       * `detectSessionInUrl` is false above and must stay false: the app reads
       * the fragment itself, at a moment it controls, and scrubs it — letting
       * auth-js race that would put a live session in the address bar.
       *
       * If PKCE is ever wanted (it is the better flow), the parser and
       * `resumeRecovery` move to `exchangeCodeForSession` in the same commit.
       * `supabaseClient.test.ts` fails if this value drifts.
       */
      flowType: 'implicit',
    },
    realtime: {
      // The crew is ~6 people; the default 10/s is an order of magnitude more
      // than the UI can generate, and a lower ceiling keeps a runaway loop from
      // tripping the project's realtime quota.
      params: { eventsPerSecond: 5 },
    },
    // P4b-02: NO custom global header. `global.headers` is merged into EVERY
    // sub-client, including `functions`, and the edge functions' shared CORS
    // allow-list (supabase/functions/_shared/cors.ts) does not name
    // `x-application-name` — so the preflight for `functions.invoke()` was
    // rejected by the browser and admin-users / steam-link could never be
    // called from the SPA. A cosmetic log tag is not worth breaking every
    // edge function; if it is ever wanted back, the allow-list must gain it
    // first.
  });
}

/** Process-wide client. Tests build their own with `createQdClient`. */
export function qdClient(): QdClient {
  client ??= createQdClient(supabaseEnv());
  return client;
}
